import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Form,
  Grid,
  Icon,
  Input,
  Loader,
  Message,
  Modal,
  Select,
  Table,
} from 'semantic-ui-react';
import MotivationLayout from '../MotivationLayout';
import ConfirmSaveModal from '../ConfirmSaveModal';
import TableStats from '../../../components/TableStats';
import {
  createMission,
  deleteMission,
  fetchMissions,
  updateMission,
} from '../api';
import {
  defaultPageRequest,
  Mission,
  MissionForm,
  missionTypeOptions,
  NAME_MAX_LENGTH,
} from '../types';
import SuccessDataSaveNotify from '../../Cards/SuccessDataSaveNotify';
import { downloadBlob } from '../../../shared/utils';
import '../../../components/FiltersComponent/filters.scss';

const emptyForm = (): MissionForm => ({
  name: '',
  description: '',
  type: 'SALES',
  targetValue: '1',
  reward: '200',
  active: true,
});

const typeSelectReadOnly = missionTypeOptions.length <= 1;

const MissionsPage = () => {
  const [items, setItems] = useState<Mission[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [nameFilter, setNameFilter] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Mission | null>(null);
  const [form, setForm] = useState<MissionForm>(emptyForm());
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof MissionForm, string>>
  >({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<Mission | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError('');
    fetchMissions(defaultPageRequest)
      .then(({ items: list, totalElements: total }) => {
        setItems(Array.isArray(list) ? list : []);
        setTotalElements(total || (list && list.length) || 0);
      })
      .catch(() => {
        setLoadError('Не удалось загрузить миссии');
        setItems([]);
        setTotalElements(0);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFieldErrors({});
    setFormOpen(true);
  };

  const openEdit = (item: Mission) => {
    setEditing(item);
    setForm({
      name: item.name || '',
      description: item.description || '',
      type: item.type || 'SALES',
      targetValue: String(
        item.targetValue !== undefined && item.targetValue !== null
          ? item.targetValue
          : 0
      ),
      reward: String(
        item.reward !== undefined && item.reward !== null ? item.reward : 0
      ),
      active: item.active !== false,
    });
    setFieldErrors({});
    setFormOpen(true);
  };

  const validate = (): boolean => {
    const errors: Partial<Record<keyof MissionForm, string>> = {};
    const trimmedName = form.name.trim();
    if (!trimmedName) {
      errors.name = 'Название обязательно';
    } else if (trimmedName.length > NAME_MAX_LENGTH) {
      errors.name = `Не более ${NAME_MAX_LENGTH} символов`;
    }
    if (!form.type.trim()) {
      errors.type = 'Тип обязателен';
    }
    const target = Number(form.targetValue);
    if (
      form.targetValue === '' ||
      Number.isNaN(target) ||
      !Number.isInteger(target) ||
      target < 1
    ) {
      errors.targetValue = 'Целое число ≥ 1';
    }
    const reward = Number(form.reward);
    if (
      form.reward === '' ||
      Number.isNaN(reward) ||
      !Number.isInteger(reward) ||
      reward < 0
    ) {
      errors.reward = 'Целое число ≥ 0';
    }
    // M-04: unique by name (same as loyalty categories); BE also enforces
    if (!errors.name) {
      const duplicate = items.some((item) => {
        if (
          editing &&
          editing.id !== undefined &&
          item.id !== undefined &&
          item.id === editing.id
        ) {
          return false;
        }
        return (
          (item.name || '').trim().toLowerCase() === trimmedName.toLowerCase()
        );
      });
      if (duplicate) {
        errors.name = 'Миссия с таким именем уже существует';
      }
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const requestSave = () => {
    if (!validate()) {
      return;
    }
    setConfirmOpen(true);
  };

  const doSave = () => {
    const body = {
      name: form.name.trim(),
      description: form.description.trim(),
      type: form.type,
      targetValue: Number(form.targetValue),
      reward: Number(form.reward),
      active: !!form.active,
    };
    setSaving(true);
    const request =
      editing && editing.id !== undefined
        ? updateMission(editing.id, body)
        : createMission(body);

    request
      .then(() => {
        setConfirmOpen(false);
        setFormOpen(false);
        SuccessDataSaveNotify('Изменения сохранены');
        load();
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const openDelete = (item: Mission) => {
    setDeletingItem(item);
    setDeleteOpen(true);
  };

  const doDelete = () => {
    if (!deletingItem || deletingItem.id === undefined) {
      return;
    }
    setSaving(true);
    deleteMission(deletingItem.id)
      .then(() => {
        setDeleteOpen(false);
        setDeletingItem(null);
        SuccessDataSaveNotify('Миссия удалена');
        load();
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const filtered = nameFilter.trim()
    ? items.filter(
        (m) =>
          (m.name || '')
            .toLowerCase()
            .indexOf(nameFilter.trim().toLowerCase()) !== -1 ||
          (m.description || '')
            .toLowerCase()
            .indexOf(nameFilter.trim().toLowerCase()) !== -1
      )
    : items;

  const onExportCsv = () => {
    const header = ['id', 'name', 'description', 'type', 'targetValue', 'reward', 'active'];
    const escape = (v: any) => {
      const s = v === undefined || v === null ? '' : String(v);
      if (/[",\n;]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const lines = [header.join(';')].concat(
      filtered.map((m) =>
        [
          m.id,
          m.name,
          m.description,
          m.type,
          m.targetValue,
          m.reward,
          m.active !== false ? 'true' : 'false',
        ]
          .map(escape)
          .join(';')
      )
    );
    const blob = new Blob(['\uFEFF' + lines.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    downloadBlob(blob, 'missions.csv');
  };

  return (
    <MotivationLayout activeTab="missions" title="Миссии">
      <Grid stackable>
        <Grid.Row>
          <Grid.Column className="table-page__stats">
            <TableStats
              totalElements={nameFilter.trim() ? filtered.length : totalElements}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={3}>
            <div className="filters">
              <Form>
                <Input
                  type="text"
                  fluid
                  placeholder="Поиск по названию"
                  value={nameFilter}
                  onChange={(_e, { value }) => setNameFilter(String(value))}
                />
                <Button
                  className="filters__button"
                  labelPosition="right"
                  icon
                  onClick={onExportCsv}
                  type="button"
                >
                  Экспорт CSV
                  <Icon name="download" />
                </Button>
                <Button
                  className="filters__button"
                  labelPosition="right"
                  icon
                  onClick={openCreate}
                  type="button"
                >
                  Добавить
                  <Icon name="plus" />
                </Button>
              </Form>
            </div>
            <p className="motivation-page__hint" style={{ marginTop: 12 }}>
              Привязка миссий к категориям лояльности — на экране «Категории»
              (кнопка «Миссии» у строки). Название миссии должно быть
              уникальным. Удаление снимает и связи с категориями.
            </p>
          </Grid.Column>
          <Grid.Column width={13}>
            {loading ? (
              <Loader active inline="centered" />
            ) : loadError ? (
              <Message negative>
                <Message.Header>{loadError}</Message.Header>
                <Button size="small" onClick={load} style={{ marginTop: 8 }}>
                  Повторить
                </Button>
              </Message>
            ) : (
              <Table celled compact selectable>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>ID</Table.HeaderCell>
                    <Table.HeaderCell>Название</Table.HeaderCell>
                    <Table.HeaderCell>Описание</Table.HeaderCell>
                    <Table.HeaderCell>Тип</Table.HeaderCell>
                    <Table.HeaderCell>Цель</Table.HeaderCell>
                    <Table.HeaderCell>Награда</Table.HeaderCell>
                    <Table.HeaderCell>Активна</Table.HeaderCell>
                    <Table.HeaderCell width={2}>Действия</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filtered.length === 0 ? (
                    <Table.Row>
                      <Table.Cell colSpan={8} textAlign="center">
                        Миссии не найдены
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    filtered.map((item) => (
                      <Table.Row
                        key={item.id !== undefined ? item.id : item.name}
                      >
                        <Table.Cell>{item.id}</Table.Cell>
                        <Table.Cell
                          title={item.name}
                          style={{
                            maxWidth: 180,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.name}
                        </Table.Cell>
                        <Table.Cell
                          title={item.description}
                          style={{
                            maxWidth: 220,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.description}
                        </Table.Cell>
                        <Table.Cell>{item.type}</Table.Cell>
                        <Table.Cell>{item.targetValue}</Table.Cell>
                        <Table.Cell>{item.reward}</Table.Cell>
                        <Table.Cell>{item.active ? 'Да' : 'Нет'}</Table.Cell>
                        <Table.Cell>
                          <Button size="mini" onClick={() => openEdit(item)}>
                            Изменить
                          </Button>{' '}
                          <Button
                            size="mini"
                            negative
                            basic
                            onClick={() => openDelete(item)}
                          >
                            Удалить
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))
                  )}
                </Table.Body>
              </Table>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <Modal open={formOpen} onClose={() => !saving && setFormOpen(false)}>
        <Modal.Header>
          {editing ? 'Редактирование миссии' : 'Новая миссия'}
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Input
              label={`Название (макс. ${NAME_MAX_LENGTH})`}
              value={form.name}
              maxLength={NAME_MAX_LENGTH}
              error={fieldErrors.name || false}
              onChange={(_e, { value }) =>
                setForm((p) => ({ ...p, name: String(value) }))
              }
            />
            <Form.TextArea
              label="Описание"
              value={form.description}
              onChange={(_e, { value }) =>
                setForm((p) => ({ ...p, description: String(value || '') }))
              }
            />
            <Form.Field error={!!fieldErrors.type}>
              <label>Тип</label>
              {typeSelectReadOnly ? (
                <Input value={form.type || 'SALES'} readOnly disabled />
              ) : (
                <Select
                  options={missionTypeOptions}
                  value={form.type}
                  onChange={(_e, { value }) =>
                    setForm((p) => ({ ...p, type: String(value) }))
                  }
                />
              )}
            </Form.Field>
            <Form.Input
              label="Цель (кол-во продаж)"
              type="number"
              min={1}
              step={1}
              value={form.targetValue}
              error={fieldErrors.targetValue || false}
              onChange={(_e, { value }) =>
                setForm((p) => ({ ...p, targetValue: String(value) }))
              }
            />
            <Form.Input
              label="Награда (баллы)"
              type="number"
              min={0}
              step={1}
              value={form.reward}
              error={fieldErrors.reward || false}
              onChange={(_e, { value }) =>
                setForm((p) => ({ ...p, reward: String(value) }))
              }
            />
            <Form.Field>
              <Checkbox
                toggle
                label="Активна"
                checked={form.active}
                onChange={(_e, { checked }) =>
                  setForm((p) => ({ ...p, active: !!checked }))
                }
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>
            Отмена
          </Button>
          <Button primary onClick={requestSave} disabled={saving}>
            Сохранить
          </Button>
        </Modal.Actions>
      </Modal>

      <ConfirmSaveModal
        open={confirmOpen}
        loading={saving}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={doSave}
      />

      <ConfirmSaveModal
        open={deleteOpen}
        loading={saving}
        title="Удалить миссию?"
        content={
          deletingItem
            ? `Удалить миссию «${deletingItem.name}»? Связи с категориями будут сняты. Это действие нельзя отменить.`
            : 'Удалить миссию?'
        }
        confirmLabel="Удалить"
        confirmNegative
        onCancel={() => {
          if (!saving) {
            setDeleteOpen(false);
            setDeletingItem(null);
          }
        }}
        onConfirm={doDelete}
      />
    </MotivationLayout>
  );
};

export default MissionsPage;
